// Chargements du dépôt (E2) : ordres émis à confirmer, confirmation du chargement (quantité servie, compartiments,
// scellés, température et densité) qui crée la sortie validée et la ligne de transit et lève la réservation ;
// dérogation tracée accordée par une personne habilitée quand le disponible du propriétaire est insuffisant (D-026).

import { useState } from 'react';
import { Link, useParams } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { deProduit } from '@carburanttrack/shared/logistique';
import { api, ErreurApi, messageErreur } from '../../api/client';
import { Message, MessageErreur, Squelette, Vide } from '../../composants/Etats';
import { Icone } from '../../composants/Icones';
import { dateHeure } from '../../composants/format';
import { localVersIso, maintenantLocal, useNomProduit } from '../stocks/composants';
import { AfficherPlus, EtiquetteEtatOrdre, LiensBons, quantite, resumeOrdre } from './communs';
import type { FicheStock, ResumeInstallation } from '../stocks/types';
import type { FicheOrdre, LigneOrdre } from './types';

interface VueChargements {
  aConfirmer: LigneOrdre[];
  confirmesDuJour: LigneOrdre[];
}

export function PageChargements() {
  const q = useQuery({ queryKey: ['chargements'], queryFn: async () => (await api<VueChargements>('/api/v1/chargements')).donnees, refetchInterval: 120_000 });
  const maintenant = Date.now();
  // Chargements confirmés du jour : dix lignes puis dix de plus à la demande.
  const [nbConfirmes, setNbConfirmes] = useState(10);
  return (
    <div>
      <div className="page-tete">
        <div>
          <h1>Chargements</h1>
          <p className="attenue">Ordres de transport émis par les distributeurs pour vos dépôts. La confirmation crée la sortie validée et la ligne de transit, et lève la réservation.</p>
        </div>
      </div>
      {q.isLoading && <Squelette lignes={5} libelle="Chargement des ordres à confirmer" />}
      {q.error && <MessageErreur erreur={q.error} titre="Chargements indisponibles" />}
      {q.data && (
        <>
          <section className="carte-bloc" aria-labelledby="titre-a-confirmer">
            <div className="carte-bloc-tete">
              <h2 id="titre-a-confirmer">À confirmer</h2>
              <span className="attenue">{q.data.aConfirmer.length} ordre{q.data.aConfirmer.length > 1 ? 's' : ''}</span>
            </div>
            {q.data.aConfirmer.length === 0 ? (
              <Vide>Aucun chargement à confirmer.</Vide>
            ) : (
              <ul className="liste-lignes">
                {q.data.aConfirmer.map((o) => {
                  const enRetard = Date.parse(o.dateChargementPrevue) < maintenant;
                  return (
                    <li key={o.id}>
                      <Link to={`/chargements/${o.id}`}>
                        <span>
                          <b>
                            {o.numero}, {quantite(o.volumePrevu, o.unite)} {deProduit(o.produitCode)} {o.prioritaire && <span className="etiquette rouge">Prioritaire</span>} {enRetard && <span className="etiquette ambre">Heure prévue dépassée</span>}
                          </b>
                          <small>
                            {o.proprietaire.nom}, {o.origine.nom} vers {o.destination.nom} ; prévu {dateHeure(o.dateChargementPrevue)} ; {o.transporteur.nom}, {o.vehicule ? `citerne ${o.vehicule.immatriculation}` : 'véhicule non encore affecté'}
                          </small>
                        </span>
                        <Icone nom="chevron" />
                      </Link>
                    </li>
                  );
                })}
              </ul>
            )}
          </section>
          <section className="carte-bloc" aria-labelledby="titre-confirmes">
            <div className="carte-bloc-tete">
              <h2 id="titre-confirmes">Confirmés aujourd’hui</h2>
              <span className="attenue">{q.data.confirmesDuJour.length} chargement{q.data.confirmesDuJour.length > 1 ? 's' : ''}</span>
            </div>
            {q.data.confirmesDuJour.length === 0 ? (
              <p className="attenue">Aucun chargement confirmé aujourd’hui.</p>
            ) : (
              <ul className="liste-lignes">
                {q.data.confirmesDuJour.slice(0, nbConfirmes).map((o) => (
                  <li key={o.id}>
                    <Link to={`/livraisons/${o.id}`}>
                      <span>
                        <b>
                          {o.numero}, {quantite(o.quantiteChargee ?? o.volumePrevu, o.unite)} servis <EtiquetteEtatOrdre etat={o.etat} libelle={o.etatLibelle} />
                        </b>
                        <small>
                          {o.proprietaire.nom}, vers {o.destination.nom}, chargé {dateHeure(o.dateChargement)}
                        </small>
                      </span>
                      <Icone nom="chevron" />
                    </Link>
                  </li>
                ))}
              </ul>
            )}
            <AfficherPlus affiches={Math.min(nbConfirmes, q.data.confirmesDuJour.length)} total={q.data.confirmesDuJour.length} pas={10} libelle="chargements" onPlus={() => setNbConfirmes((n) => n + 10)} />
          </section>
          <details className="carte-bloc">
            <summary>Accorder une dérogation préalable à l’émission d’un ordre</summary>
            <DerogationPrealable />
          </details>
        </>
      )}
    </div>
  );
}

interface ReservoirDepot {
  id: string;
  numero: string;
  produitCode: string;
  unite: string;
  capacite: string;
}

export function PageConfirmerChargement() {
  const { id } = useParams();
  const client = useQueryClient();
  const ordre = useQuery({ queryKey: ['ordre-transport', id], queryFn: async () => (await api<FicheOrdre>(`/api/v1/ordres-transport/${id}`)).donnees, enabled: Boolean(id) });
  const bacs = useQuery({
    queryKey: ['bacs-depot', ordre.data?.origine.id],
    queryFn: async () => (await api<{ reservoirs: ReservoirDepot[] }>(`/api/v1/stocks/installations/${ordre.data!.origine.id}`)).donnees.reservoirs,
    enabled: Boolean(ordre.data),
  });
  const [bac, setBac] = useState('');
  const [quantiteServie, setQuantiteServie] = useState('');
  const [compartiments, setCompartiments] = useState<Record<number, { volume: string; scelle: string }>>({});
  const [quand, setQuand] = useState(maintenantLocal);
  const [temperature, setTemperature] = useState('');
  const [densite, setDensite] = useState('');
  const [observations, setObservations] = useState('');
  const [derogationId, setDerogationId] = useState<string | null>(null);
  const [motifDerogation, setMotifDerogation] = useState('');
  const [insuffisant, setInsuffisant] = useState<string | null>(null);
  const corps = () => {
    const o = ordre.data!;
    const comps = o.compartiments.map((c) => ({ numero: c.numero, volumeCharge: (compartiments[c.numero]?.volume ?? '').replace(/\s/g, '').replace(',', '.') || '0', scelle: compartiments[c.numero]?.scelle.trim() || null }));
    const total = comps.reduce((s, c) => s + Number(c.volumeCharge), 0);
    return {
      reservoirId: bac,
      quantite: quantiteServie.trim() ? quantiteServie.replace(/\s/g, '').replace(',', '.') : String(total),
      compartiments: comps,
      scelles: comps.map((c) => c.scelle).filter((s): s is string => Boolean(s)),
      horodatage: localVersIso(quand),
      temperature: temperature.trim() ? temperature.replace(',', '.') : null,
      densite: densite.trim() ? densite.replace(',', '.') : null,
      observations: observations.trim() || null,
      derogationId,
    };
  };
  const confirmer = useMutation({
    mutationFn: async () => api(`/api/v1/ordres-transport/${id}/chargement`, { corps: corps() }),
    onError: (e) => setInsuffisant(e instanceof ErreurApi && e.statut === 409 ? e.message : null),
    onSuccess: () => {
      setInsuffisant(null);
      void client.invalidateQueries({ queryKey: ['ordre-transport', id] });
      void client.invalidateQueries({ queryKey: ['chargements'] });
    },
  });
  const deroger = useMutation({
    mutationFn: async () => {
      const o = ordre.data!;
      const d = corps();
      return (await api<{ id: string }>('/api/v1/stocks/derogations', { corps: { installationId: o.origine.id, produitCode: o.produitCode, proprietaireId: o.proprietaire.id, quantite: d.quantite, motif: motifDerogation, ordreTransportId: o.id } })).donnees;
    },
    onSuccess: (d) => setDerogationId(d.id),
  });
  if (ordre.isLoading) return <Squelette lignes={6} />;
  if (ordre.error || !ordre.data) return <MessageErreur erreur={ordre.error} titre="Ordre de transport indisponible" />;
  const o = ordre.data;
  if (o.etat !== 'emis')
    return (
      <div>
        <h1>{o.numero}</h1>
        <Message type="succes">
          Chargement confirmé {o.dateChargement ? `le ${dateHeure(o.dateChargement)}` : ''} : {o.quantiteChargee ? quantite(o.quantiteChargee, o.unite) : 'quantité indisponible'} servis.
        </Message>
        <p>
          <LiensBons id={o.id} numero={o.numero} bonChargement={o.droits.bonChargement} bonLivraison={o.droits.bonLivraison} />
        </p>
        <Link className="bouton" to="/chargements">
          Retour aux chargements
        </Link>
      </div>
    );
  if (!o.droits.confirmerChargement) return <Message type="avertissement">Le chargement de cet ordre est confirmé par le dépôt expéditeur.</Message>;
  const bacsProduit = (bacs.data ?? []).filter((b) => b.produitCode === o.produitCode);
  return (
    <form
      aria-labelledby="titre-chargement"
      onSubmit={(e) => {
        e.preventDefault();
        confirmer.mutate();
      }}
    >
      <div className="page-tete">
        <div>
          <span className="attenue">{o.origine.nom}</span>
          <h1 id="titre-chargement">Confirmer le chargement de {o.numero}</h1>
          <p className="attenue">
            {resumeOrdre(o)} ; propriétaire {o.proprietaire.nom} ; {o.transporteur.nom}, {o.vehiculeDetail ? `citerne ${o.vehiculeDetail.immatriculation}` : 'véhicule non affecté'}, {o.conducteurDetail?.nom ?? 'conducteur non affecté'}.
          </p>
        </div>
      </div>
      <fieldset className="carte-bloc">
        <legend>Bac et quantité servie</legend>
        <div className="champ">
          <label htmlFor="ch-bac">Bac de sortie</label>
          <select id="ch-bac" value={bac} onChange={(e) => setBac(e.target.value)} required>
            <option value="">Choisir</option>
            {bacsProduit.map((b) => (
              <option key={b.id} value={b.id}>
                Bac {b.numero}, capacité {quantite(b.capacite, b.unite)}
              </option>
            ))}
          </select>
          {bacs.error && <span className="aide">Bacs indisponibles : {messageErreur(bacs.error)}</span>}
        </div>
        {o.compartiments.map((c) => (
          <div className="formulaire-ligne serre" key={c.numero}>
            <div className="champ">
              <label htmlFor={`ch-c${c.numero}`}>Compartiment {c.numero} (prévu {quantite(c.volumePrevu, o.unite)})</label>
              <input id={`ch-c${c.numero}`} inputMode="decimal" value={compartiments[c.numero]?.volume ?? ''} onChange={(e) => setCompartiments({ ...compartiments, [c.numero]: { scelle: compartiments[c.numero]?.scelle ?? '', volume: e.target.value } })} required />
            </div>
            <div className="champ">
              <label htmlFor={`ch-s${c.numero}`}>Scellé</label>
              <input id={`ch-s${c.numero}`} value={compartiments[c.numero]?.scelle ?? ''} onChange={(e) => setCompartiments({ ...compartiments, [c.numero]: { volume: compartiments[c.numero]?.volume ?? '', scelle: e.target.value } })} maxLength={40} />
            </div>
          </div>
        ))}
        <div className="champ">
          <label htmlFor="ch-total">Quantité servie au compteur (litres)</label>
          <input id="ch-total" inputMode="decimal" value={quantiteServie} onChange={(e) => setQuantiteServie(e.target.value)} placeholder="Somme des compartiments si vide" />
        </div>
        <div className="formulaire-ligne serre">
          <div className="champ">
            <label htmlFor="ch-temp">Température (°C)</label>
            <input id="ch-temp" inputMode="decimal" value={temperature} onChange={(e) => setTemperature(e.target.value)} />
          </div>
          <div className="champ">
            <label htmlFor="ch-densite">Densité à 15 °C</label>
            <input id="ch-densite" inputMode="decimal" value={densite} onChange={(e) => setDensite(e.target.value)} />
          </div>
          <div className="champ">
            <label htmlFor="ch-quand">Heure du chargement</label>
            <input id="ch-quand" type="datetime-local" value={quand} onChange={(e) => setQuand(e.target.value)} required />
          </div>
        </div>
        <div className="champ">
          <label htmlFor="ch-obs">Observations</label>
          <input id="ch-obs" value={observations} onChange={(e) => setObservations(e.target.value)} maxLength={1000} />
        </div>
      </fieldset>
      {insuffisant && (
        <fieldset className="carte-bloc">
          <legend>Dérogation (D-026)</legend>
          <Message type="avertissement">{insuffisant}</Message>
          {derogationId ? (
            <Message type="info">Dérogation accordée et tracée (référence {derogationId}). Confirmez à nouveau le chargement.</Message>
          ) : (
            <>
              <div className="champ">
                <label htmlFor="ch-derog">Motif de la dérogation</label>
                <input id="ch-derog" value={motifDerogation} onChange={(e) => setMotifDerogation(e.target.value)} minLength={3} maxLength={500} />
                <span className="aide">Réservée à une personne habilitée du dépôt ; elle est inscrite au journal d’audit et signalée comme anomalie à régulariser.</span>
              </div>
              <button type="button" className="bouton" disabled={deroger.isPending || motifDerogation.trim().length < 3} onClick={() => deroger.mutate()}>
                Accorder la dérogation
              </button>
              {deroger.error && <Message type="erreur">{messageErreur(deroger.error)}</Message>}
            </>
          )}
        </fieldset>
      )}
      {confirmer.error && !insuffisant && <Message type="erreur">{messageErreur(confirmer.error)}</Message>}
      <div className="actions">
        <button type="submit" className="bouton primaire large" disabled={confirmer.isPending || !bac}>
          Confirmer le chargement
        </button>
        <Link className="bouton" to="/chargements">
          Retour
        </Link>
      </div>
    </form>
  );
}

/**
 * Dérogation préalable à l'émission d'un ordre (D-026) : le distributeur dont l'émission est refusée pour
 * disponible insuffisant la demande au dépôt ; la personne habilitée du dépôt l'accorde ici, avec un motif, et
 * en communique la référence. Les dépôts et les positions proposés sont ceux que le serveur rend lisibles.
 */
export function DerogationPrealable() {
  const nomProduit = useNomProduit();
  const depots = useQuery({ queryKey: ['stocks-installations', 'depots'], queryFn: async () => (await api<ResumeInstallation[]>('/api/v1/stocks/installations')).donnees });
  const [installationId, setInstallationId] = useState('');
  const fiche = useQuery({ queryKey: ['fiche-stock', installationId], queryFn: async () => (await api<FicheStock>(`/api/v1/stocks/installations/${installationId}`)).donnees, enabled: Boolean(installationId) });
  const [position, setPosition] = useState('');
  const [quantiteDemandee, setQuantiteDemandee] = useState('');
  const [motif, setMotif] = useState('');
  const choisie = fiche.data?.proprietaires.find((p) => p.cle === position) ?? null;
  const accorder = useMutation({
    mutationFn: async () =>
      (await api<{ id: string }>('/api/v1/stocks/derogations', { corps: { installationId, proprietaireId: choisie!.proprietaireId, produitCode: choisie!.produitCode, quantite: quantiteDemandee.replace(/\s/g, '').replace(',', '.'), motif } })).donnees,
  });
  const lisibles = (depots.data ?? []).filter((d) => ['depot', 'centre_stockage', 'raffinerie'].includes(d.categorie));
  return (
    <form
      className="carte-bloc formulaire-ligne"
      aria-labelledby="titre-derogation"
      onSubmit={(e) => {
        e.preventDefault();
        accorder.mutate();
      }}
    >
      <h2 id="titre-derogation" className="pleine-ligne">
        Dérogation préalable à l’émission d’un ordre (D-026)
      </h2>
      {depots.error && <MessageErreur erreur={depots.error} />}
      <div className="champ">
        <label htmlFor="dp-inst">Dépôt</label>
        <select
          id="dp-inst"
          value={installationId}
          onChange={(e) => {
            setInstallationId(e.target.value);
            setPosition('');
          }}
          required
        >
          <option value="">Choisir</option>
          {lisibles.map((d) => (
            <option key={d.id} value={d.id}>
              {d.nom}
            </option>
          ))}
        </select>
      </div>
      <div className="champ">
        <label htmlFor="dp-position">Propriétaire et produit</label>
        <select id="dp-position" value={position} onChange={(e) => setPosition(e.target.value)} required disabled={!fiche.data}>
          <option value="">Choisir</option>
          {(fiche.data?.proprietaires ?? []).map((p) => (
            <option key={p.cle} value={p.cle}>
              {p.proprietaireNom}, {nomProduit(p.produitCode)}, disponible {p.disponible === null ? 'indisponible' : quantite(p.disponible, p.unite)}
            </option>
          ))}
        </select>
      </div>
      <div className="champ">
        <label htmlFor="dp-q">Quantité couverte (litres)</label>
        <input id="dp-q" inputMode="decimal" value={quantiteDemandee} onChange={(e) => setQuantiteDemandee(e.target.value)} required />
      </div>
      <div className="champ pleine-ligne">
        <label htmlFor="dp-motif">Motif</label>
        <input id="dp-motif" value={motif} onChange={(e) => setMotif(e.target.value)} required minLength={3} maxLength={500} />
        <span className="aide">Inscrit au journal d’audit et signalé comme anomalie à régulariser.</span>
      </div>
      <div className="actions pleine-ligne">
        <button type="submit" className="bouton primaire" disabled={accorder.isPending || !choisie || motif.trim().length < 3 || !quantiteDemandee.trim()}>
          Accorder la dérogation
        </button>
      </div>
      {accorder.data && <Message type="succes">Dérogation accordée. Référence à communiquer au distributeur : {accorder.data.id}</Message>}
      {accorder.error && <Message type="erreur">{messageErreur(accorder.error)}</Message>}
    </form>
  );
}
